import React, { useEffect, useMemo, useState } from "react";
import {
  cancelAppointment,
  getAppointmentsByUserId,
  updateAppointment,
} from "../appointment/AppointmentServices";
import Modal from "../../utilities/components/Modal";
import KeycloakService from "../config/KeycloakService";
import AppointmentForm from "../appointment/AppointmentForm";
import Cancellation from "../../utilities/components/Cancellation";
import { AppointmentModel } from "../../utilities/models/AppointmentModel";
import { useLoadingSpinner } from "../../utilities/components/LoadingSpinnerProvider";
import { EntitiesListResponseModel } from "../../utilities/models/EntitiesListResponseModel";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import AppointmentCard from "./management/AppointmentCard";

/**
 * The component to manage the user's appointments.
 * @returns The component to manage the user's appointments.
 */
const AppointmentManagement = () => {
  const setIsLoading = useLoadingSpinner();
  const [state, setState] = useState(false);
  const [objectId, setObjectId] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [params, setParams] = useState({ size: 9, page: 0 }); // Grid size 9
  const [appointment, setAppointment] = useState(AppointmentModel);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [appointments, setAppointments] = useState(EntitiesListResponseModel);
  const [recentlyChangedAppointment, setRecentlyChangedAppointment] = useState(null);

  const successMessage = useMemo(() => {
    if (!recentlyChangedAppointment) {
      return "";
    }

    return recentlyChangedAppointment.action === "created"
      ? "Your new appointment has been scheduled and highlighted below."
      : "Your appointment changes were saved and highlighted below.";
  }, [recentlyChangedAppointment]);

  const lunchEditionModal = (appointment) => {
    setAppointment(appointment);
    setIsEditModalOpen(true);
  };

  const lunchCreationModal = () => {
    setIsCreateModalOpen(true);
  };

  const lunchCancellationModal = (id) => {
    setObjectId(id);
    setIsCancelModalOpen(true);
  };

  const cancelAppointmentHandler = async () => {
    setApiError(false);
    setIsLoading(true);

    try {
      await cancelAppointment(objectId, setState, setIsLoading, setApiError);
      setIsCancelModalOpen(false);
    } catch {
      // Error feedback is handled in the service layer.
    }
  };

  const handleAppointmentSuccess = (savedAppointment, action) => {
    if (!savedAppointment) {
      return;
    }

    setRecentlyChangedAppointment({
      id: savedAppointment.id,
      dateTime: savedAppointment.dateTime,
      action,
      timestamp: Date.now(),
    });
  };

  useEffect(() => {
    if (!recentlyChangedAppointment) {
      return undefined;
    }

    const timeoutId = globalThis.setTimeout(() => {
      setRecentlyChangedAppointment(null);
    }, 5000);

    return () => globalThis.clearTimeout(timeoutId);
  }, [recentlyChangedAppointment]);

  useEffect(() => {
    setApiError(false);
    setIsLoading(true);

    getAppointmentsByUserId(
      setAppointments,
      KeycloakService.getUserId(),
      setIsLoading,
      setApiError,
      params,
    ).catch(() => {
      // Error feedback is handled in the service layer.
    });
  }, [state, setIsLoading, params]);

  useEffect(() => {
    if (!recentlyChangedAppointment?.id) {
      return;
    }

    const highlightedCard = document.querySelector(
      `[data-appointment-id="${recentlyChangedAppointment.id}"]`,
    );

    highlightedCard?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }, [appointments, recentlyChangedAppointment]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < appointments.totalPages) {
      setParams((prev) => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            My Appointments
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            View and manage your scheduled appointments.
          </p>
        </div>

        <button
          onClick={lunchCreationModal}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm"
        >
          <Plus size={20} />
          <span>New Appointment</span>
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-sm dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
          {successMessage}
        </div>
      )}

      {/* Grid Content */}
      {appointments.content.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {appointments.content.map((appt) => (
            <AppointmentCard
              key={appt.id}
              appointment={appt}
              onEdit={lunchEditionModal}
              onCancel={(id) => lunchCancellationModal(id)}
              isHighlighted={recentlyChangedAppointment?.id === appt.id}
              highlightLabel={
                recentlyChangedAppointment?.id === appt.id
                  ? recentlyChangedAppointment.action
                  : null
              }
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 border-dashed">
          <p className="text-slate-500 dark:text-slate-400 text-lg">
            {apiError
              ? "We couldn’t load your appointments right now. Please try again shortly."
              : "You have no upcoming appointments."}
          </p>
          <button
            onClick={lunchCreationModal}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            Schedule an appointment &rarr;
          </button>
        </div>
      )}

      {/* Pagination */}
      {appointments.totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() => handlePageChange(params.page - 1)}
            disabled={params.page === 0}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
            Page {params.page + 1} of {appointments.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(params.page + 1)}
            disabled={params.page >= appointments.totalPages - 1}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      {/* Modals */}
      <Modal isOpen={isEditModalOpen} onClose={setIsEditModalOpen}>
        <AppointmentForm
          appointment={appointment}
          updateAppointment={updateAppointment}
          isUpdating={true}
          toggleModal={setIsEditModalOpen}
          setState={setState}
          onSuccess={handleAppointmentSuccess}
        />
      </Modal>
      <Modal isOpen={isCreateModalOpen} onClose={setIsCreateModalOpen}>
        <AppointmentForm
          toggleModal={setIsCreateModalOpen}
          setState={setState}
          onSuccess={handleAppointmentSuccess}
        />
      </Modal>
      <Modal isOpen={isCancelModalOpen} onClose={setIsCancelModalOpen}>
        <Cancellation
          actionHandler={cancelAppointmentHandler}
          setOpen={setIsCancelModalOpen}
          title="Cancel Appointment"
          warningMessage="Are you sure you want to cancel this appointment?"
          actionText="Cancel Appointment"
          cancelText="Go Back"
        />
      </Modal>
    </div>
  );
};

export default AppointmentManagement;
